/**
 * CraftSection — "from the workshop": real workshop photos and the three
 * things that make the slippers, with a link to the heel spur article.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { RevealWords } from "./RevealWords";
import { SectionLinkLabel, sectionLinkClass } from "./SectionHeader";

// Workshop photos (Postolar Varga, uploaded to Shopify Files 2026-10-02).
const CDN = "https://cdn.shopify.com/s/files/1/0991/8090/4831/files/";
const HEEL_PHOTO = `${CDN}papuca-s-dodatkom-i-bez-dodatka-za-petni-trn.jpg?v=1790957301`;
const CLOG_PHOTO = `${CDN}kozna-papuca-vargice-s-dodatkom-za-petni-trn.jpg?v=1790957301`;

const POINTS = ["handmade", "insole", "custom"] as const;

export function CraftSection({ locale }: Readonly<{ locale: string }>) {
  const { t } = useTranslation();

  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8 max-mobile:py-10">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <div className="relative pb-16 max-mobile:pb-10" data-reveal="items">
          <div className="aspect-16/10 overflow-hidden rounded-3xl bg-background-5 outline-1 -outline-offset-1 outline-black/10">
            <img
              src={HEEL_PHOTO}
              alt={t("Homepage.craft.heelAlt")}
              width={1600}
              height={900}
              loading="lazy"
              className="size-full object-cover"
            />
          </div>
          <div className="absolute right-6 bottom-0 aspect-4/3 w-[42%] overflow-hidden rounded-2xl bg-background-5 shadow-[0_12px_32px_-12px_rgb(0_0_0/0.35)] outline-4 outline-background-6 max-mobile:right-3">
            <img
              src={CLOG_PHOTO}
              alt={t("Homepage.craft.clogAlt")}
              width={1600}
              height={900}
              loading="lazy"
              className="size-full object-cover"
            />
          </div>
        </div>

        <div>
          <p className="text-small font-semibold text-primary">
            {t("Homepage.craft.kicker")}
          </p>
          <h2
            className="mt-3 text-xlarge leading-tight font-bold tracking-[-0.01em] text-text-1 max-mobile:text-large"
            data-reveal="words"
          >
            <RevealWords text={t("Homepage.craft.title")} />
          </h2>
          <ul className="mt-8 space-y-6" data-reveal="items">
            {POINTS.map((key, index) => (
              <li key={key} className="flex gap-4">
                <span className="mt-0.5 text-small font-semibold text-text-4 tabular-nums">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-base font-semibold text-text-1">
                    {t(`Homepage.craft.points.${key}.title`)}
                  </h3>
                  <p className="mt-1 text-small text-pretty text-text-3">
                    {t(`Homepage.craft.points.${key}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <Link
            to="/$locale/blog/$blogHandle/$articleHandle"
            params={{
              locale,
              blogHandle: "postolar-varga-blog",
              articleHandle: "papuce-za-petni-trn",
            }}
            className={`${sectionLinkClass} mt-8`}
          >
            <SectionLinkLabel label={t("Homepage.craft.cta")} />
          </Link>
        </div>
      </div>
    </section>
  );
}
